import { useState } from "react";
import {
  X,
  Search,
  Type,
  Grid3X3,
  Layers,
  ListChecks,
  Quote,
  Image,
  Globe,
  MousePointer,
  FileText,
  BarChart3,
  Check,
} from "lucide-react";
import { AVAILABLE_SECTIONS } from "../../../config/templateConfig";
import { INFOGRAPHIC_SECTIONS } from "../../../config/infographicTemplate";
import { axiosInstance } from "../../../lib/axios";

interface Props {
  onClose: () => void;
  onAdd: (section: any) => void;
  existingSections: string[];
  /** Which catalogue of sections to offer — the templates differ. */
  templateId?: string;
}

// The infographic template has its own section vocabulary; everything else
// keeps the original catalogue.
const catalogueFor = (templateId?: string) =>
  templateId === "infographic"
    ? INFOGRAPHIC_SECTIONS.map((s) => ({
        id: s.id,
        type: s.type,
        title: s.title,
        description: s.description,
        icon: s.icon,
        defaultContent: s.defaultContent,
        layouts: ["default"],
      }))
    : AVAILABLE_SECTIONS;

const iconMap: Record<string, any> = {
  Info: Type,
  Target: Grid3X3,
  AlertTriangle: Layers,
  Lightbulb: ListChecks,
  Route: ListChecks,
  FileCheck: Grid3X3,
  Star: Quote,
  Briefcase: Image,
  Globe: Globe,
  Handshake: MousePointer,
  FileText: FileText,
  BarChart: BarChart3,
};

const AddSectionModal = ({ onClose, onAdd, existingSections, templateId }: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSection, setSelectedSection] = useState<any>(null);

  const filteredSections = catalogueFor(templateId).filter(
    (section) =>
      section.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      section.type.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const [adding, setAdding] = useState(false);

  // Portfolio sections are seeded with the agency's OWN work (Portfolio
  // Library) rather than stock imagery, so a client only ever sees real work.
  const handleAdd = async () => {
    if (!selectedSection) return;
    const isPortfolio = String(selectedSection.type || "").toLowerCase().includes("portfolio");
    if (!isPortfolio) {
      onAdd(selectedSection);
      return;
    }
    setAdding(true);
    try {
      const { data } = await axiosInstance.get("/proposals/portfolio-assets");
      const assets = (data?.assets || data || []).slice(0, 3);
      onAdd({
        ...selectedSection,
        defaultContent: {
          ...(selectedSection.defaultContent || {}),
          projects: assets.map((a: any) => ({ title: a.name || "", image: a.url || "" })),
        },
      });
    } catch {
      onAdd(selectedSection); // library unavailable — add the empty section
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col shadow-2xl">

        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h3 className="text-xl font-bold text-gray-800">Add Section</h3>
            <p className="text-sm text-gray-500 mt-1">
              Choose a section type to add to your proposal
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 border-b border-gray-100">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search sections..."
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-3">
            {filteredSections.map((section) => {
              const Icon = iconMap[section.icon] || Type;
              const alreadyExists = existingSections.some((id) =>
                id.startsWith(section.id)
              );

              return (
                <button
                  key={section.id}
                  onClick={() => setSelectedSection(section)}
                  className={`relative flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                    selectedSection?.id === section.id
                      ? "border-emerald-500 bg-emerald-50"
                      : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                  }`}
                >

                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
                      selectedSection?.id === section.id
                        ? "bg-emerald-500 text-white"
                        : "bg-gray-100 text-gray-500"
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-800 truncate">
                        {section.title}
                      </p>
                      {alreadyExists && (
                        <span className="px-1.5 py-0.5 bg-gray-100 text-gray-500 text-[10px] rounded">
                          Added
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                      {section.description}
                    </p>
                    <div className="flex items-center gap-1 mt-2">
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-full">
                        {section.type}
                      </span>
                      <span className="text-[10px] text-gray-400">
                        • {section.layouts.length} layouts
                      </span>
                    </div>
                  </div>

                  {selectedSection?.id === section.id && (
                    <div className="absolute top-2 right-2 w-5 h-5 bg-emerald-500 text-white rounded-full flex items-center justify-center">
                      <Check className="w-3 h-3" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {filteredSections.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <Search className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No sections found</p>
              <p className="text-sm">Try a different search term</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-4 border-t border-gray-200 bg-gray-50">
          <div className="text-sm text-gray-500">
            {selectedSection ? (
              <span>
                Selected: <strong>{selectedSection.title}</strong>
              </span>
            ) : (
              "Select a section to add"
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleAdd}
              disabled={adding || !selectedSection}
              className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold transition-all ${
                selectedSection
                  ? "bg-emerald-500 text-white hover:bg-emerald-600"
                  : "bg-gray-200 text-gray-400 cursor-not-allowed"
              }`}
            >
              Add Section
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSectionModal;
